import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Check } from "lucide-react";
import HeroDemo from "@/components/landing/HeroDemo";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.06 },
  },
};

const item = {
  hidden: { opacity: 0, y: 16, filter: "blur(6px)" },
  show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { type: "spring", stiffness: 300, damping: 30 } },
};

const ORBS = [
  { color: "rgba(181,82,56,0.10)", size: 320, x: "-12%", y: "18%", delay: 0 },
  { color: "rgba(20,184,166,0.07)", size: 260, x: "78%", y: "8%", delay: 4 },
  { color: "rgba(244,63,94,0.05)", size: 200, x: "55%", y: "65%", delay: 8 },
];

export default function V1HeroSection({ handleGoogleLogin }) {
  return (
    <section className="relative min-h-[min(100svh,880px)] flex flex-col items-center overflow-hidden pt-32 sm:pt-36 lg:pt-40 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-8 bg-[#FAF7F8]">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-rose-50/[0.25] to-stone-50/80" aria-hidden />

      {/* Ambient floating orbs */}
      {ORBS.map((orb, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: orb.size,
            height: orb.size,
            left: orb.x,
            top: orb.y,
            background: `radial-gradient(circle, ${orb.color}, transparent 70%)`,
            filter: "blur(40px)",
          }}
          animate={{
            x: [0, 20, -15, 0],
            y: [0, -12, 8, 0],
            scale: [1, 1.06, 0.96, 1],
          }}
          transition={{ duration: 14 + i * 3, repeat: Infinity, ease: "easeInOut", delay: orb.delay }}
          aria-hidden
        />
      ))}

      {/* Radial glow behind headline */}
      <div
        className="absolute left-1/2 top-[20%] -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{
          width: "min(800px, 90vw)",
          height: 400,
          background: "radial-gradient(ellipse 80% 60% at 50% 50%, rgba(181,82,56,0.07), transparent 65%)",
        }}
        aria-hidden
      />

      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: `linear-gradient(rgba(148, 163, 184, 0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 0.10) 1px, transparent 1px)`,
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 40%, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 40%, black 30%, transparent 75%)",
        }}
        aria-hidden
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col items-center">
        <motion.div
          className="flex flex-col items-center text-center w-full"
          variants={container}
          initial="hidden"
          animate="show"
        >
          <motion.h1
            variants={item}
            className="max-w-4xl text-[2.35rem] font-bold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-[3.25rem] xl:text-[3.5rem]"
          >
            Built for AEs who can&apos;t afford
            <br />
            <span className="animate-text-shimmer bg-gradient-to-r from-[#7A3324] via-[#B55238] to-[#C86B52] bg-clip-text text-transparent">
              a sloppy call.
            </span>
          </motion.h1>

          <motion.p variants={item} className="mt-6 max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Your personal AI agent - so you walk into every sales call prepared, handle the toughest moments with
            confidence, and close faster
          </motion.p>

          <motion.div variants={item} className="mt-10 flex w-full max-w-md flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center">
            <Link
              to="/demo"
              className="group relative inline-flex h-12 items-center justify-center gap-2 overflow-hidden rounded-full bg-[#B55238] px-8 text-sm font-semibold text-white shadow-lg shadow-[#B55238]/25 transition-all hover:shadow-xl hover:shadow-[#B55238]/30 hover:scale-[1.02]"
            >
              <span className="absolute inset-0 overflow-hidden rounded-full">
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:animate-[btn-shimmer_0.8s_ease-out_forwards]" />
              </span>
              <Play className="relative h-4 w-4 text-white" strokeWidth={2.25} />
              <span className="relative">Watch live demo</span>
            </Link>
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="inline-flex h-12 items-center justify-center rounded-full border border-slate-200/80 bg-white/80 px-7 text-sm font-semibold text-slate-800 shadow-sm backdrop-blur-sm transition-all hover:border-slate-300 hover:bg-white hover:shadow-[0_0_20px_rgba(0,0,0,0.06)]"
            >
              Continue with Google
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          className="relative w-full max-w-4xl mt-14 sm:mt-16"
          initial={{ opacity: 0, y: 28, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 0.75, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          <div
            className="absolute inset-0 -m-8 rounded-[2.5rem] opacity-60"
            style={{
              background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(181,82,56,0.12), transparent 70%)",
              filter: "blur(40px)",
            }}
            aria-hidden
          />

          <div className="relative rounded-2xl border border-white/60 bg-white/80 p-1 shadow-xl shadow-slate-900/[0.08] ring-1 ring-slate-200/50 backdrop-blur-sm">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100/80 bg-slate-50/60 rounded-t-[0.9rem] backdrop-blur-sm">
              <div className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
              </div>
              <span className="ml-2 text-[11px] font-medium text-slate-500 truncate flex-1 text-center sm:text-left sm:ml-4">
                shadow - Prep · Live · Recap
              </span>
              <span className="hidden sm:flex items-center gap-1.5 rounded-md bg-[#B55238]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#7A3324] ring-1 ring-[#B55238]/20">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#B55238] opacity-45" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#B55238]" />
                </span>
                Live
              </span>
            </div>

            <div className="bg-gradient-to-b from-slate-50/40 to-white p-4 sm:p-6 rounded-b-[1.35rem]">
              <div className="mb-3 flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Live call preview</p>
                <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                  <Check className="h-3 w-3 text-[#B55238]" strokeWidth={2.5} />
                  Grounded cards
                </div>
              </div>
              <HeroDemo variant="v1" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
